import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  output,
  signal,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  DateRangeFormComponent,
  LoaderButtonComponent,
  MOMENT_DATEPICKER_PROVIDERS,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import {
  ICustomReportFilter,
  IReportRun,
  QueryScopeConfigId,
  type ICustomReport,
  type IQueryScopeRequests,
  type ReportingReference,
} from '@principle-theorem/principle-core/interfaces';
import {
  REPORT_BUILDER_DATA_SOURCES,
  ReportBuilderDataSourceInstance,
  resolveDataSource,
} from '@principle-theorem/reporting';
import {
  filterUndefined,
  ITimePeriod,
  snapshot,
  toTimePeriod,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import * as moment from 'moment-timezone';
import { BehaviorSubject, ReplaySubject, type Observable } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { QueryScopeFormComponent } from '../../../report-builder/query-scope-form/query-scope-form.component';
import { QueryScopeForms } from '../../../report-builder/query-scope-form/scope-form';
import { ReportBuilderStore } from '../../../report-builder/report-builder.store';
import { CustomReportDatePickers } from './custom-report-date-pickers';
import {
  RunCustomReportForm,
  RunReportFormBloc,
} from './run-custom-report-form';

export interface IRunReportEvent {
  queryScopes: IQueryScopeRequests;
  filters: ICustomReportFilter[];
  filterDate?: moment.Moment;
  filterDateRange?: ITimePeriod;
  editMode?: boolean;
}

@Component({
  selector: 'pr-run-custom-report-form',
  templateUrl: './run-custom-report-form.component.html',
  styleUrls: ['./run-custom-report-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  imports: [
    DateRangeFormComponent,
    LoaderButtonComponent,
    MatDatepickerModule,
    MatInputModule,
    NgCommonModule,
    QueryScopeFormComponent,
  ],
})
export class RunCustomReportFormComponent {
  private _organisation = inject(OrganisationService);
  private _historicalRun = signal<WithRef<IReportRun> | undefined>(undefined);

  store = inject(ReportBuilderStore);

  formData$ = new BehaviorSubject<IQueryScopeRequests | undefined>(undefined);
  customReport$ = new ReplaySubject<WithRef<ICustomReport>>(1);
  form = new RunCustomReportForm();
  formBloc: RunReportFormBloc;
  submitDisabled$: Observable<boolean>;
  queryableTimestamp$: Observable<ReportingReference | undefined>;
  dataSource$: Observable<ReportBuilderDataSourceInstance>;
  dateRange$ = new ReplaySubject<ITimePeriod>(1);
  editCustomReportPermissions = [
    ReportingPermissions.ReportingCustomReportEdit,
  ];
  staticFilters: Signal<ICustomReportFilter[]>;
  modifiedFilters = signal<ICustomReportFilter[]>([]);
  datePickers: CustomReportDatePickers;
  dateRangeChange = output<ITimePeriod>();
  runReport = output<IRunReportEvent>();
  queryScopeForms: QueryScopeForms;
  promptToRun$: Observable<boolean>;

  @Input()
  set customReport(customReport: WithRef<ICustomReport>) {
    if (customReport) {
      this.customReport$.next(customReport);
    }
  }

  @Input() disabled = false;

  isViewingHistoricalRun = computed(() => !!this._historicalRun());

  @Input()
  set historicalRun(run: WithRef<IReportRun> | undefined) {
    this._historicalRun.set(run);
    if (run) {
      this._applyHistoricalRunValues(run);
      this._disableAllForms();
    } else {
      this._enableAllForms();
    }
  }

  @Input()
  set dateRange(dateRange: ITimePeriod) {
    if (dateRange) {
      this.dateRange$.next(dateRange);
    }
  }

  constructor() {
    this.queryScopeForms = new QueryScopeForms(
      this._organisation.userBrands$,
      this._organisation.userPractices$,
      this._organisation.brand$
    );

    const customReport = toSignal(this.customReport$, {
      initialValue: undefined,
    });
    this.staticFilters = computed(() => customReport()?.staticFilters ?? []);
    this.datePickers = new CustomReportDatePickers(this.staticFilters);

    this.formBloc = new RunReportFormBloc(this._organisation);
    this.formBloc.brands$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((brands) => {
        const brand = first(brands);
        if (!brand) {
          return;
        }
        return this.form.controls.brand.setValue(brand);
      });
    this.formBloc.practices$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((practices) =>
        this.form.controls.practices.setValue(practices)
      );

    this.submitDisabled$ = this.form.valueChanges.pipe(
      map(() => this.form.valid),
      startWith(false),
      map((isValid) => !isValid)
    );

    this.dataSource$ = this.customReport$.pipe(
      map((report) =>
        resolveDataSource(report.dataSource, REPORT_BUILDER_DATA_SOURCES)
      ),
      filterUndefined()
    );

    this.promptToRun$ = this.store.state$.pipe(
      map((state) => !state.loading && !state.results)
    );
  }

  async submit(editMode: boolean): Promise<void> {
    if (!this.form.valid) {
      return;
    }

    const dataSource = await snapshot(this.dataSource$);
    const queryScopes = (await snapshot(this.formData$)) ?? {};

    if (
      this._isScopeRequired(dataSource, QueryScopeConfigId.Brand) &&
      !queryScopes.brand
    ) {
      queryScopes.brand = await this.queryScopeForms.brand.getDefaultValue();
    }

    if (
      this._isScopeRequired(dataSource, QueryScopeConfigId.Practice) &&
      !queryScopes.practice
    ) {
      queryScopes.practice =
        await this.queryScopeForms.practice.getDefaultValue();
    }

    const dateForm = this.datePickers.formGroup.value;
    this.runReport.emit({
      editMode,
      filters: [],
      filterDate: dateForm.date,
      filterDateRange: toTimePeriod(
        dateForm.dateFrom ?? moment(),
        dateForm.dateTo ?? moment()
      ),
      queryScopes,
    });
  }

  private _applyHistoricalRunValues(run: WithRef<IReportRun>): void {
    const reportSnapshot = run.reportSnapshot;
    if (reportSnapshot?.queryScopes?.brand) {
      this.queryScopeForms.brand.formGroup.patchValue(reportSnapshot.queryScopes.brand);
    }
    if (reportSnapshot?.queryScopes?.practice) {
      this.queryScopeForms.practice.formGroup.patchValue(
        reportSnapshot.queryScopes.practice
      );
    }

    if (reportSnapshot?.filterDate) {
      this.datePickers.formGroup.patchValue({
        date: moment(reportSnapshot.filterDate.toDate()),
      });
    }
    if (reportSnapshot?.filterDateRange) {
      this.datePickers.formGroup.patchValue({
        dateFrom: moment(reportSnapshot.filterDateRange.from.toDate()),
        dateTo: moment(reportSnapshot.filterDateRange.to.toDate()),
      });
    }
  }

  private _isScopeRequired(
    dataSource: ReportBuilderDataSourceInstance,
    scopeId: QueryScopeConfigId
  ): boolean {
    return dataSource.factTable.scopes.some((scope) => scope.id === scopeId);
  }

  private _disableAllForms(): void {
    this.datePickers.formGroup.disable();
    this.queryScopeForms.brand.formGroup.disable();
    this.queryScopeForms.practice.formGroup.disable();
  }

  private _enableAllForms(): void {
    this.datePickers.formGroup.enable();
    this.queryScopeForms.brand.formGroup.enable();
    this.queryScopeForms.practice.formGroup.enable();
  }
}
